import { useState, useEffect } from 'react';
import { sharepointAPI } from '../lib/api';
import {
  FiFolder, FiLoader, FiCheck, FiX, FiSearch, FiAlertCircle,
  FiDatabase, FiGlobe, FiChevronRight, FiArrowLeft, FiTrash2, FiPlus
} from 'react-icons/fi';

/**
 * SharePointBrowser
 *
 * Two-level browsing:
 *  - Level 1: list of sites (search + select)
 *    - "Add All" → auto-discover all drives for selected sites (existing behaviour)
 *    - "Select Libraries" per site → drill into Level 2
 *  - Level 2: list of document libraries for a site
 *    - Select individual drives and "Add Selected"
 *
 * The component also shows currently-connected sites/drives so the user can
 * remove individual entries without having to reconnect everything.
 */
export default function SharePointBrowser({ connection, onComplete, onCancel }) {
  // ── Level-1 state (site list) ──────────────────────────────────────────────
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // ── Level-2 state (drive list for one site) ────────────────────────────────
  const [drillSite, setDrillSite] = useState(null); // { id, name }
  const [drives, setDrives] = useState([]);
  const [selectedDrives, setSelectedDrives] = useState([]);
  const [loadingDrives, setLoadingDrives] = useState(false);

  // ── Shared state ───────────────────────────────────────────────────────────
  const [discovering, setDiscovering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);

  // Connected sites that can be removed (starts from what the connection already has)
  const [connectedSites, setConnectedSites] = useState(
    () => deduplicateDrives(connection.connected_sites || [])
  );

  // ── Selection state for connected panel ───────────────────────────────────
  // Set of drive_ids the user has ticked in the connected panel
  const [selectedConnected, setSelectedConnected] = useState(new Set());

  // ── Helpers ────────────────────────────────────────────────────────────────
  function deduplicateDrives(entries) {
    const seen = new Set();
    return entries.filter(e => {
      const key = e.drive_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // ── Load sites on mount / search ──────────────────────────────────────────
  useEffect(() => {
    loadSites();
  }, [connection.id]);

  const loadSites = async () => {
    try {
      setLoadingSites(true);
      setError(null);
      const response = await sharepointAPI.getSites(connection.id, searchQuery);
      setSites(response.data.sites || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingSites(false);
    }
  };

  // ── Level-2: load drives for a site ──────────────────────────────────────
  const openDrillDown = async (site) => {
    setDrillSite(site);
    setSelectedDrives([]);
    setDrives([]);
    setLoadingDrives(true);
    setError(null);
    try {
      const response = await sharepointAPI.getDrives(connection.id, site.id);
      setDrives(response.data.drives || []);
      // Pre-select drives that are already connected for this site
      const alreadyConnected = connectedSites
        .filter(c => c.site_id === site.id)
        .map(c => c.drive_id);
      setSelectedDrives(alreadyConnected);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingDrives(false);
    }
  };

  const closeDrillDown = () => {
    setDrillSite(null);
    setDrives([]);
    setSelectedDrives([]);
    setError(null);
  };

  const toggleDrive = (driveId) => {
    setSelectedDrives(prev =>
      prev.includes(driveId) ? prev.filter(id => id !== driveId) : [...prev, driveId]
    );
  };

  // Add selected drives for the drilled-in site to connectedSites, then save
  const handleAddSelectedDrives = async () => {
    if (selectedDrives.length === 0) return;

    // Build new entries for selected drives
    const newEntries = drives
      .filter(d => selectedDrives.includes(d.id))
      .map(d => ({
        site_id: drillSite.id,
        site_name: drillSite.name || drillSite.displayName,
        drive_id: d.id,
        drive_name: d.name,
        folder_id: null,
        folder_name: null,
      }));

    // Merge with existing (remove old entries for this site, then add selected ones)
    const withoutThisSite = connectedSites.filter(c => c.site_id !== drillSite.id);
    const updated = deduplicateDrives([...withoutThisSite, ...newEntries]);

    await persistConnectedSites(updated);
    closeDrillDown();
  };

  // ── Level-1: auto-discover all drives for selected sites ──────────────────
  const handleAutoDiscover = async () => {
    if (selectedSites.length === 0) {
      setError('Please select at least one site to discover');
      return;
    }
    try {
      setDiscovering(true);
      setError(null);
      setDiscoveryResult(null);
      const response = await sharepointAPI.autoDiscover(connection.id, selectedSites);
      // Merge auto-discovered entries with existing
      const discovered = response.data.connected_sites || [];
      const merged = deduplicateDrives([...connectedSites, ...discovered]);
      setConnectedSites(merged);
      setDiscoveryResult({ ...response.data, connected_sites: discovered });
      setSelectedSites([]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDiscovering(false);
    }
  };

  // ── Connected-panel selection helpers ────────────────────────────────────
  const toggleConnectedDrive = (driveId) => {
    setSelectedConnected(prev => {
      const next = new Set(prev);
      next.has(driveId) ? next.delete(driveId) : next.add(driveId);
      return next;
    });
  };

  const toggleConnectedSite = (siteId) => {
    const siteDriverIds = connectedSites.filter(c => c.site_id === siteId).map(c => c.drive_id);
    const allSelected = siteDriverIds.every(id => selectedConnected.has(id));
    setSelectedConnected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        siteDriverIds.forEach(id => next.delete(id));
      } else {
        siteDriverIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectAllConnected = () => {
    const allIds = connectedSites.map(c => c.drive_id);
    const allSelected = allIds.every(id => selectedConnected.has(id));
    setSelectedConnected(allSelected ? new Set() : new Set(allIds));
  };

  const handleRemoveSelected = async () => {
    const updated = connectedSites.filter(c => !selectedConnected.has(c.drive_id));
    await persistConnectedSites(updated);
    setSelectedConnected(new Set());
  };

  // ── Remove a single drive from connected list ─────────────────────────────
  const handleRemoveDrive = async (driveId) => {
    const updated = connectedSites.filter(c => c.drive_id !== driveId);
    await persistConnectedSites(updated);
    setSelectedConnected(prev => { const next = new Set(prev); next.delete(driveId); return next; });
  };

  // ── Persist to backend ────────────────────────────────────────────────────
  const persistConnectedSites = async (entries) => {
    try {
      setSaving(true);
      setError(null);
      await sharepointAPI.saveSelection(connection.id, entries);
      setConnectedSites(entries);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Site selection helpers ────────────────────────────────────────────────
  const toggleSiteSelection = (site) => {
    const exists = selectedSites.find(s => s.id === site.id);
    if (exists) {
      setSelectedSites(selectedSites.filter(s => s.id !== site.id));
    } else {
      setSelectedSites([...selectedSites, { id: site.id, name: site.displayName || site.name }]);
    }
  };
  const isSiteSelected = (siteId) => selectedSites.some(s => s.id === siteId);

  // Group connectedSites by site name for display
  const groupedConnected = connectedSites.reduce((acc, c) => {
    const key = c.site_name || 'Unknown Site';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Level-2 drill-down (drive selection for one site)
  // ══════════════════════════════════════════════════════════════════════════
  if (drillSite) {
    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={closeDrillDown}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Back to sites"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Select Document Libraries</h2>
            <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1">
              <FiGlobe className="w-4 h-4 text-blue-500" />
              {drillSite.displayName || drillSite.name}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Drive list */}
        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[450px] overflow-y-auto">
          {loadingDrives ? (
            <div className="flex justify-center items-center py-12">
              <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : drives.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FiDatabase className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p>No document libraries found for this site</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {/* Select All */}
              {drives.length > 1 && (
                <div
                  className="flex items-center p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    const allIds = drives.map(d => d.id);
                    const allSelected = allIds.every(id => selectedDrives.includes(id));
                    setSelectedDrives(allSelected ? [] : allIds);
                  }}
                >
                  <div className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                    drives.every(d => selectedDrives.includes(d.id))
                      ? 'bg-blue-600 border-blue-600'
                      : drives.some(d => selectedDrives.includes(d.id))
                        ? 'bg-blue-300 border-blue-300'
                        : 'border-gray-300'
                  }`}>
                    {drives.some(d => selectedDrives.includes(d.id)) && (
                      <FiCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({drives.length} libraries)
                  </span>
                </div>
              )}
              {drives.map(drive => (
                <div
                  key={drive.id}
                  className={`flex items-center p-4 cursor-pointer transition-colors ${
                    selectedDrives.includes(drive.id) ? 'bg-blue-50 hover:bg-blue-100' : 'bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDrive(drive.id)}
                >
                  <div className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                    selectedDrives.includes(drive.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {selectedDrives.includes(drive.id) && <FiCheck className="w-3 h-3 text-white" />}
                  </div>
                  <FiDatabase className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{drive.name}</p>
                    {drive.driveType && (
                      <p className="text-xs text-gray-400 capitalize">{drive.driveType}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
          <button
            onClick={closeDrillDown}
            disabled={saving}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAddSelectedDrives}
            disabled={saving || selectedDrives.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          >
            {saving ? (
              <><FiLoader className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><FiPlus className="w-4 h-4 mr-2" />Add {selectedDrives.length > 0 ? `${selectedDrives.length} ` : ''}Selected</>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Level-1 (site list + connected sites management)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Manage SharePoint Sites</h2>
          <p className="text-sm text-gray-600 mt-1">
            {connection.tenant_name} — select sites and document libraries to make searchable
          </p>
        </div>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <FiX className="w-6 h-6" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ── Currently connected sites ─────────────────────────────────────── */}
      {connectedSites.length > 0 && (
        <div className="border border-green-200 rounded-lg overflow-hidden">
          {/* Panel header: select-all + action buttons */}
          <div className="bg-green-50 px-4 py-2.5 flex items-center gap-3 border-b border-green-200">
            {/* Select-all checkbox */}
            <div
              className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                connectedSites.every(c => selectedConnected.has(c.drive_id))
                  ? 'bg-green-600 border-green-600'
                  : connectedSites.some(c => selectedConnected.has(c.drive_id))
                    ? 'bg-green-300 border-green-300'
                    : 'border-gray-400'
              }`}
              onClick={toggleSelectAllConnected}
              title="Select all"
            >
              {connectedSites.some(c => selectedConnected.has(c.drive_id)) && (
                <FiCheck className="w-2.5 h-2.5 text-white" />
              )}
            </div>

            <h3 className="text-sm font-semibold text-green-800 flex items-center gap-1.5 flex-1">
              <FiCheck className="w-4 h-4" />
              Connected ({connectedSites.length} librar{connectedSites.length !== 1 ? 'ies' : 'y'})
              {selectedConnected.size > 0 && (
                <span className="text-green-600 font-normal">· {selectedConnected.size} selected</span>
              )}
            </h3>

            <div className="flex items-center gap-2 flex-shrink-0">
              {saving && <FiLoader className="w-4 h-4 animate-spin text-green-600" />}
              {selectedConnected.size > 0 ? (
                <button
                  onClick={handleRemoveSelected}
                  disabled={saving}
                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40 transition-colors font-medium"
                >
                  Remove selected ({selectedConnected.size})
                </button>
              ) : (
                <button
                  onClick={() => persistConnectedSites([])}
                  disabled={saving}
                  className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40 transition-colors"
                >
                  Remove all
                </button>
              )}
            </div>
          </div>

          {/* Site groups */}
          <div className="divide-y divide-green-100 max-h-56 overflow-y-auto">
            {Object.entries(groupedConnected).map(([siteName, siteConnections]) => {
              const siteId = siteConnections[0].site_id;
              const siteDriveIds = siteConnections.map(c => c.drive_id);
              const allSiteSelected = siteDriveIds.every(id => selectedConnected.has(id));
              const someSiteSelected = siteDriveIds.some(id => selectedConnected.has(id));
              return (
                <div key={siteName} className="p-3">
                  {/* Site row with site-level checkbox */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                        allSiteSelected
                          ? 'bg-blue-600 border-blue-600'
                          : someSiteSelected
                            ? 'bg-blue-300 border-blue-300'
                            : 'border-gray-300'
                      }`}
                      onClick={() => toggleConnectedSite(siteId)}
                      title="Select all libraries in this site"
                    >
                      {someSiteSelected && <FiCheck className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1 flex-1 min-w-0">
                      <FiGlobe className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{siteName}</span>
                    </p>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openDrillDown({ id: siteId, name: siteName, displayName: siteName })}
                        disabled={saving}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 disabled:opacity-40"
                        title="Edit library selection for this site"
                      >
                        <FiFolder className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => persistConnectedSites(connectedSites.filter(c => c.site_id !== siteId))}
                        disabled={saving}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5 disabled:opacity-40"
                        title="Remove all libraries for this site"
                      >
                        <FiTrash2 className="w-3 h-3" />
                        Remove
                      </button>
                    </span>
                  </div>

                  {/* Library rows with individual checkboxes */}
                  <div className="space-y-0.5 pl-6">
                    {siteConnections.map(c => (
                      <div
                        key={c.drive_id}
                        className={`flex items-center gap-2 rounded px-1 py-0.5 cursor-pointer group transition-colors ${
                          selectedConnected.has(c.drive_id) ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleConnectedDrive(c.drive_id)}
                      >
                        <div className={`w-3.5 h-3.5 border-2 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                          selectedConnected.has(c.drive_id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedConnected.has(c.drive_id) && <FiCheck className="w-2 h-2 text-white" />}
                        </div>
                        <FiDatabase className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 flex-1 truncate">{c.drive_name}</span>
                        <button
                          onClick={e => { e.stopPropagation(); handleRemoveDrive(c.drive_id); }}
                          disabled={saving}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 transition-opacity disabled:opacity-30 flex-shrink-0"
                          title="Remove this library"
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Discovery result flash ────────────────────────────────────────── */}
      {discoveryResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Discovery complete — added {discoveryResult.stats.drivesDiscovered} librar{discoveryResult.stats.drivesDiscovered !== 1 ? 'ies' : 'y'} from {discoveryResult.stats.sitesDiscovered} site{discoveryResult.stats.sitesDiscovered !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={() => setDiscoveryResult(null)} className="ml-auto text-green-500 hover:text-green-700">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Search ───────────────────────────────────────────────────────── */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadSites()}
            placeholder="Search sites..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <button
          onClick={loadSites}
          disabled={loadingSites}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
        >
          Search
        </button>
      </div>

      {/* Selected sites chip bar */}
      {selectedSites.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-800 mb-2">Selected for auto-discover ({selectedSites.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {selectedSites.map(site => (
              <span key={site.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                <FiGlobe className="w-3 h-3" />
                {site.name}
                <button onClick={() => setSelectedSites(selectedSites.filter(s => s.id !== site.id))} className="hover:text-blue-600">
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Sites list ───────────────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
        {loadingSites ? (
          <div className="flex justify-center items-center py-12">
            <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : sites.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FiFolder className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No sites found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* Select All for auto-discover */}
            {sites.length > 1 && (
              <div
                className="flex items-center p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                onClick={() => {
                  const allSelected = sites.every(s => isSiteSelected(s.id));
                  setSelectedSites(allSelected ? [] : sites.map(s => ({ id: s.id, name: s.displayName || s.name })));
                }}
              >
                <div className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                  sites.every(s => isSiteSelected(s.id))
                    ? 'bg-blue-600 border-blue-600'
                    : sites.some(s => isSiteSelected(s.id))
                      ? 'bg-blue-300 border-blue-300'
                      : 'border-gray-300'
                }`}>
                  {sites.some(s => isSiteSelected(s.id)) && <FiCheck className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm font-medium text-gray-700">Select All ({sites.length} sites)</span>
              </div>
            )}

            {sites.map(site => (
              <div
                key={site.id}
                className={`flex items-center p-4 transition-colors ${
                  isSiteSelected(site.id) ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
                {/* Checkbox for auto-discover */}
                <div
                  className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                    isSiteSelected(site.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 hover:border-blue-400'
                  }`}
                  onClick={() => toggleSiteSelection(site)}
                >
                  {isSiteSelected(site.id) && <FiCheck className="w-3 h-3 text-white" />}
                </div>

                <FiGlobe className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />

                {/* Site info */}
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleSiteSelection(site)}>
                  <p className="font-medium text-gray-900 truncate">{site.displayName || site.name}</p>
                  {site.webUrl && <p className="text-xs text-gray-400 truncate">{site.webUrl}</p>}
                </div>

                {/* Select Libraries button */}
                <button
                  onClick={e => { e.stopPropagation(); openDrillDown(site); }}
                  className="ml-3 flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-blue-50 hover:text-blue-700 transition-colors"
                  title="Select specific document libraries for this site"
                >
                  Select Libraries
                  <FiChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Discovering overlay ───────────────────────────────────────────── */}
      {discovering && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex flex-col items-center gap-3">
          <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm font-medium text-blue-800">Discovering document libraries and subsites…</p>
          <p className="text-xs text-blue-600">This may take a moment for sites with many subsites</p>
        </div>
      )}

      {/* ── Action bar ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onCancel}
          disabled={discovering || saving}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>

        <div className="flex gap-3">
          {/* Only show Done when there are connected sites */}
          {connectedSites.length > 0 && (
            <button
              onClick={onComplete}
              disabled={discovering || saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              Done
            </button>
          )}

          <button
            onClick={handleAutoDiscover}
            disabled={discovering || saving || selectedSites.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          >
            {discovering ? (
              <><FiLoader className="w-4 h-4 mr-2 animate-spin" />Discovering…</>
            ) : (
              <><FiSearch className="w-4 h-4 mr-2" />Auto-Discover All ({selectedSites.length})</>
            )}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        <strong>Select Libraries</strong> — choose specific document libraries for a site. &nbsp;
        <strong>Auto-Discover All</strong> — tick sites above and automatically add all their libraries and subsites.
      </p>
    </div>
  );
}
